/**
 * AssetLogo — kart ızgaralarında işlem yapılan varlığın logosu.
 *
 * KAYNAK: borsanın kendisi (Binance futures varlık logosu ucu), backend üzerinden.
 * Elle indirilmiş ikon seti tutulmuyor — yeni bir parite açıldığında logo
 * kendiliğinden geliyor.
 *
 * NEDEN BACKEND ÜZERİNDEN, NEDEN DOĞRUDAN DEĞİL: logoların %70'i
 * `bin.bnbstatic.com` üzerinde ve o alan adı TARAYICIDAN yüklenmiyor — aynı URL
 * sunucudan HTTP 200 dönerken tarayıcıda onerror veriyor (12.10.2026 ölçümü:
 * BTC ve PUMP düşüyor, `public.bnbstatic.com` sorunsuz). Doğrudan bağlansaydık en
 * sık kullanılan kripto logoları sessizce kaybolurdu. Backend indirip 40 px'e
 * küçültüyor ve veri URI gönderiyor: dış istek yok, CORS yok, sızıntı yok
 * (7 varlık = 20 KB; ham hâli 120 KB idi).
 *
 * LOGOSUZ KALANA MONOGRAM: ölçümde 32 varlığın 30'u bulundu; kalan ikisi
 * (STONK, ANSEM) ve ileride çıkacak her yeni sembol için kırık resim yerine,
 * sembolden türetilen SABİT renkli daire içinde 2-3 harf. Hiçbir koşulda boş
 * kutu görünmez.
 */
import React, { useEffect, useState } from 'react';

// Borsalar arası ad farkı: Extended/Lighter emtiayı adıyla yazıyor, Binance kodla.
const ALIAS: Record<string, string> = {
  GOLD: 'XAU', SILVER: 'XAG', BRENTOIL: 'CL', OIL: 'CL', PLATINUM: 'XPT',
};

/** Her borsanın kendi yazımından baz varlığı çıkarır.
 *  PERP_XAG_USDC → XAG · xyz:BRENTOIL-PERP → CL · SOLUSDTM → SOL · BTCUSD1 → BTC */
export function baseAsset(symbol: string): string {
  let x = (symbol || '').toUpperCase().replace(/^[A-Z]+:/, '');   // xyz: / para: öneki
  x = x.replace('PERP_', '').replace('_USDC', '');
  x = x.replace(/-?PERP$/, '');
  x = x.replace(/(USDT|USDC|USD1|USD)M?$/, '');                   // kotasyon eki (M: KuCoin)
  return ALIAS[x] || x;
}

/** Sembolden sabit renk — aynı varlık her yerde aynı renkte görünsün. */
function monogramRenk(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return `hsl(${h % 360} 45% 32%)`;
}

const LS_KEY = 'bq_asset_logos_v1';

function lsYukle(): Record<string, string> {
  try {
    const d = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
    return d && typeof d === 'object' ? d : {};
  } catch { return {}; }
}

// Harita TÜM bileşenler arasında tek ve oturumlar arası kalıcı: her kart ayrı
// istek atmasın, her sayfa açılışında 85 KB yeniden inmesin.
let _map: Record<string, string> = lsYukle();
const _istenen = new Set<string>();          // tekrar tekrar sormayı önler
const _dinleyiciler = new Set<() => void>();

/** Logo haritasını yönetir; `symbols` içindeki EKSİK varlıkları backend'den ister. */
export function useAssetLogos(
  sendWs?: (m: object) => void,
  subscribe?: (fn: (d: any) => void) => () => void,
  symbols: string[] = [],
) {
  const [, setTik] = useState(0);

  useEffect(() => {
    const bildir = () => setTik(v => v + 1);
    _dinleyiciler.add(bildir);
    const unsub = subscribe?.((d: any) => {
      if (d?.type !== 'asset_logos' || !d.payload || typeof d.payload !== 'object') return;
      _map = { ..._map, ...d.payload };
      try { localStorage.setItem(LS_KEY, JSON.stringify(_map)); } catch {}
      _dinleyiciler.forEach(f => f());
    });
    return () => { _dinleyiciler.delete(bildir); unsub?.(); };
  }, [subscribe]);

  const anahtar = symbols.join(',');
  useEffect(() => {
    if (!sendWs) return;
    const gerekli = Array.from(new Set(symbols.map(baseAsset)))
      .filter(a => a && !_map[a] && !_istenen.has(a));
    if (!gerekli.length) return;
    gerekli.forEach(a => _istenen.add(a));
    sendWs({ type: 'get_asset_logos', payload: { assets: gerekli } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anahtar, sendWs]);

  return _map;
}

export default function AssetLogo({ symbol, size = 20 }: { symbol: string; size?: number }) {
  const base = baseAsset(symbol);
  const src = _map[base];
  // Veri URI bozuksa bile monograma düşebilmek için; sembol değişince sıfırlanır.
  const [hata, setHata] = useState(false);
  useEffect(() => { setHata(false); }, [symbol]);

  const ortak: React.CSSProperties = { width: size, height: size, borderRadius: '50%', flexShrink: 0 };

  if (src && !hata) {
    return <img src={src} alt={base} title={base}
      style={{ ...ortak, objectFit: 'cover' }} onError={() => setHata(true)} />;
  }
  const harf = base.slice(0, base.length <= 3 ? base.length : 3);
  return (
    <span title={base} style={{
      ...ortak, background: monogramRenk(base), color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.max(7, Math.round(size * (harf.length > 2 ? 0.34 : 0.42))),
      fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1,
    }}>{harf}</span>
  );
}
